"use client";

import { Compass, Mail, Phone } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
export default function Privacypolicy() {
  const header = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const quote = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const divider = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const contact = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const disciplines = [{
    number: "01",
    title: "Ethical Data Curation",
    description: "We treat your personal information as a sacred trust, curating only the essential data required to deliver a bespoke shopping experience and secure, seamless logistics.",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722769/img/privacy_data_collection.webp",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722769/img/privacy_data_collection.webp-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722769/img/privacy_data_collection.webp-wvc-sizes",
    alt: "Minimalist digital data security concept",
    span: "lg:col-span-5",
    height: "lg:h-[520px]",
    offset: ""
  }, {
    number: "02",
    title: "Fortified Transactional Integrity",
    description: "Your financial legacy is safeguarded by elite-tier encryption protocols, ensuring that every acquisition at Lush Fashion remains an entirely private affair.",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722769/img/privacy_secure_payment.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722769/img/privacy_secure_payment.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722769/img/privacy_secure_payment.png-wvc-sizes",
    alt: "Secure digital payment interface",
    span: "lg:col-span-4",
    height: "lg:h-[420px]",
    offset: "lg:mt-24"
  }, {
    number: "03",
    title: "Sovereignty & Absolute Clarity",
    description: "You retain ultimate sovereignty over your digital footprint. We operate with radical transparency, providing you with complete oversight of your stored information.",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722769/img/privacy_transparency.webp",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722769/img/privacy_transparency.webp-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722769/img/privacy_transparency.webp-wvc-sizes",
    alt: "Clear document representing user rights",
    span: "lg:col-span-3",
    height: "lg:h-[580px]",
    offset: ""
  }];
  return <section data-section-id="3193" id="privacypolicyoverview" className="bg-background text-foreground py-24 md:py-32 lg:py-[200px] overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-8 items-end mb-24 md:mb-32">
          <div ref={header.ref} className={`lg:col-span-7 transition-all duration-[900ms] ease-out ${header.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
            <p className="font-sans text-xs md:text-sm tracking-[0.3em] uppercase text-muted-foreground mb-8 font-medium">
              Chapter 01 — The Covenant of Trust
            </p>
            <h1 className="font-default font-bold text-5xl md:text-6xl lg:text-[84px] leading-[1.02] tracking-tighter text-foreground">
              Your privacy,
              <br />
              <span className="italic text-accent">
                our highest honor.
              </span>
            </h1>
          </div>

          <div ref={quote.ref} className={`lg:col-span-5 lg:text-right transition-all duration-[1100ms] delay-200 ease-out ${quote.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
            <p className="font-default italic text-2xl md:text-3xl leading-tight text-muted-foreground max-w-sm lg:ml-auto">
              "Transparency is the ultimate luxury in a digital world."
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 md:gap-10">
          {disciplines.map((item, i) => {
          const card = useRevealOnScroll<HTMLDivElement>({
            threshold: 0.15
          });
          return <div data-index={i} ref={card.ref} key={i} className={`group ${item.span} ${item.offset} transition-all duration-[900ms] ease-out ${card.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`} style={{
            transitionDelay: `${i * 120}ms`
          }}>
                <article className={`relative h-full ${item.height} bg-card text-card-foreground border border-border rounded-lg shadow-md overflow-hidden flex flex-col transition-all duration-500 ease-out group-hover:-translate-y-2 group-hover:shadow-xl`}>
                  <div className="overflow-hidden">
                    <AspectRatio ratio={4 / 5}>
                      <img src={item.image} data-wvc-srcset={item.srcset} data-wvc-sizes={item.sizes} alt={item.alt} loading="lazy" decoding="async" className="h-full w-full object-cover transition-transform duration-[800ms] ease-out group-hover:scale-[1.05]" />
                    </AspectRatio>
                  </div>

                  <div className="flex flex-col flex-1 p-8 md:p-10">
                    <div className="h-px w-full bg-border mb-6" />
                    <p className="font-sans text-xs tracking-[0.25em] uppercase text-muted-foreground mb-5 font-medium">
                      {item.number} / 03
                    </p>
                    <h3 className="relative font-default font-bold text-2xl md:text-[32px] leading-tight text-foreground mb-4 inline-block w-fit">
                      {item.title}
                      <span className="absolute left-0 -bottom-1 h-px w-0 bg-accent transition-all duration-500 ease-out group-hover:w-full" />
                    </h3>
                    <p className="text-base md:text-lg leading-relaxed text-muted-foreground">
                      {item.description}
                    </p>
                  </div>
                </article>
              </div>;
        })}
        </div>

        <div ref={divider.ref} className={`mt-32 md:mt-48 flex items-center gap-8 transition-all duration-[1000ms] ease-out ${divider.isVisible ? "opacity-100" : "opacity-0"}`}>
          <div className="h-px flex-1 bg-border" />
          <Compass className="size-6 text-accent" strokeWidth={1.25} aria-hidden="true" />
          <div className="h-px flex-1 bg-border" />
        </div>

        <div ref={contact.ref} className={`mt-32 md:mt-40 text-center transition-all duration-[1000ms] delay-300 ease-out ${contact.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <h2 className="font-default font-bold text-3xl md:text-5xl mb-12 tracking-tight">
            Concierge Privacy Support
          </h2>
          <div className="flex flex-col md:flex-row items-center justify-center gap-12 md:gap-24 mb-16">
            <div className="flex flex-col items-center gap-4">
              <div className="p-4 rounded-lg bg-accent/5">
                <Mail className="size-8 text-accent" strokeWidth={1.25} />
              </div>
              <div className="flex flex-col gap-1">
                <Link to="mailto:support@lushfashionstore.com" className="text-lg md:text-xl font-medium text-foreground hover:text-accent transition-colors underline underline-offset-4 decoration-accent/30">
                  support@lushfashionstore.com
                </Link>
                <Link to="mailto:info@lushfashionstore.com" className="text-lg md:text-xl font-medium text-foreground hover:text-accent transition-colors underline underline-offset-4 decoration-accent/30">
                  info@lushfashionstore.com
                </Link>
              </div>
            </div>
            <div className="flex flex-col items-center gap-4">
              <div className="p-4 rounded-lg bg-accent/5">
                <Phone className="size-8 text-accent" strokeWidth={1.25} />
              </div>
              <Link to="tel:4025244600" className="text-lg md:text-xl font-bold text-foreground hover:text-accent transition-colors tracking-wider">
                (402) 524-4600
              </Link>
            </div>
          </div>
          <Button asChild className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-lg px-12 py-8 h-auto text-xl font-bold transition-all active:translate-x-[2px] active:translate-y-[2px] active:shadow-none shadow-lg">
            <Link to="/CustomerCarePage">
              Visit Customer Care
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}